import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { LanguagesService } from './languages.service';
import { ITask } from './user-service';

export type InboxType = 'myTasks' | 'teamTasks' | 'groupTasks' | 'tasks' | 'signals' | 'recovery';
export interface InboxFilters {
    [key: string]: any;
}

@Injectable({
    providedIn: 'root',
})
export class FilterService {
    inboxType!: InboxType;
    filters: InboxFilters = {};
    queryParams: Params = {};
    savedFilter: boolean = false;

    private router = inject(Router);
    private languageService = inject(LanguagesService);

    getInboxTypeFromRoute(route: ActivatedRouteSnapshot): InboxType {
        this.inboxType = route.params['inboxType'] as InboxType;
        return this.inboxType;
    }

    getFiltersFromQueryParams(params: Params, inboxFilter: any[]): any[] {
        const finalObj: any = {};

        inboxFilter.forEach((filter) => {
            const key = filter.name;
            if (!key) return;

            const base: any = {
                type: filter.type,
                mapping: filter.mapping,
                mappingType: filter.mappingType,
                label: filter.label,
            };

            // ----- SEARCH -----
            if (filter.type === 'search') {
                base.value = params[key] || null;
                base._exactMatch = params.hasOwnProperty(`${key}_exactMatch`)
                    ? params[`${key}_exactMatch`] === 'true'
                    : filter.filterOptions?._exactMatch || false;
            }

            // ----- NUMBER RANGE -----
            else if (filter.type === 'number-range') {
                base.minLabel = params[`${key}_min`] ?? filter.filterOptions?.minLabel ?? '';
                base.maxLabel = params[`${key}_max`] ?? filter.filterOptions?.maxLabel ?? '';
            }

            // ----- DATE RANGE -----
            else if (filter.type === 'date-range') {
                const from = params[`${key}_from`];
                const to = params[`${key}_to`];

                base.value = from && to ? [from, to] : filter.value || null;
            }

            // ----- ENUM -----
            else if (filter.type === 'enum') {
                const opts = filter.filterOptions.options || [];
                const selectedFromParams = params[key]?.split(',') || [];

                opts.forEach((option: string | number) => {
                    base[option] = selectedFromParams.includes(option) ? true : false;
                });

                base._exactMatch = params.hasOwnProperty(`${key}_exactMatch`)
                    ? params[`${key}_exactMatch`] === 'true'
                    : filter.filterOptions?._exactMatch || false;
            }

            // ----- BOOLEAN -----
            else if (filter.type === 'boolean') {
                const opts: string[] = filter.filterOptions.options || ['True', 'False'];
                const selected = params[key];

                opts.forEach((option: string) => {
                    if (!selected) base[option] = false;
                    else base[option] = selected.toLowerCase() === option.toLowerCase();
                });
            }

            finalObj[key] = base;
        });

        return finalObj;
    }

    generateQueryParams(filterData: Record<string, any>): Params {
        let params: Params = {};
        filterData = this.languageService.translateConfig(filterData);
        for (const key in filterData) {
            let filter = filterData[key];

            if (filter.type === 'search' && filter.value && filter.value.length > 0) {
                params[key] = filter.value;
                params[`${key}_exactMatch`] = !!filter._exactMatch;
            } else if (filter.type === 'number-range') {
                if (filter.minLabel && filter.maxLabel) {
                    params[`${key}_min`] = filter.minLabel;
                    params[`${key}_max`] = filter.maxLabel;
                } else if (filter.minLabel) params[`${key}_min`] = filter.minLabel;
                else if (filter.maxLabel) params[`${key}_max`] = filter.maxLabel;
            } else if (filter.type === 'date-range' && Array.isArray(filter.value) && filter.value.length === 2) {
                if (filter.value[0] && filter.value[1]) {
                    params[`${key}_from`] = filter.value[0];
                    params[`${key}_to`] = filter.value[1];
                }
            } else if (filter.type === 'enum' || filter.type === 'boolean') {
                const selectedValues = [];

                for (const subKey in filter) {
                    const reservedKeys = ['type', 'mapping', 'mappingType', 'label', 'value', '_exactMatch'];
                    if (!reservedKeys.includes(subKey) && filter[subKey] === true) {
                        if (filter.type === 'boolean') {
                            selectedValues.push(subKey.toLowerCase());
                        } else {
                            selectedValues.push(subKey);
                        }
                    }
                }

                if (selectedValues.length > 0) {
                    params[key] = selectedValues.join(',');
                    if (filter.type === 'enum') {
                        params[`${key}_exactMatch`] = !!filter?._exactMatch;
                    }
                }
            }
        }

        return JSON.parse(JSON.stringify(params));
    }

    updateUrl(filters: InboxFilters = {}, selectedTask?: ITask, ActivatedRoute?: ActivatedRoute): void {
        const projectId =
            ActivatedRoute?.snapshot?.queryParams?.['projectId'] || window.alpha?.['project']?.projectId || null;
        const filterQueryParam: any = {};
        if (selectedTask?.['filterId'] || selectedTask?.['filterObject'])
            filterQueryParam.saved_filter = selectedTask['name'];
        this.queryParams = {
            ...filterQueryParam,
            ...(this.generateQueryParams(filters) || {}),
        };
        this.router.navigate(['../', this.inboxType], {
            relativeTo: ActivatedRoute,
            queryParams: {
                projectId,
                ...this.queryParams,
            },
        });
    }

    initFromRoute(route: ActivatedRoute, defaultFilterData: InboxFilters[]): void {
        const { projectId, ...cleanParams } = route.snapshot.queryParams;
        this.queryParams = cleanParams;
        this.filters = this.getFiltersFromQueryParams(cleanParams, defaultFilterData);
    }

    applyFilter(filter: ITask, route: ActivatedRoute, projectId: string) {
        this.router.navigate(['../', filter.key], {
            relativeTo: route,
            queryParams: { projectId },
            replaceUrl: true,
            queryParamsHandling: 'preserve',
        });
    }
}
